<template>
  <div
    ref="headerRef"
    class="bg-white shadow-md border-solid border-t border-b overflow-auto z-20 w-full sticky -top-7"
    :style="{ height: `${minHeight}px` }"
  >
    <div class="py-3 px-6 font-semibold sticky -top-1 bg-white z-10">
      Technicians
    </div>
    <div class="relative w-full flex flex-col gap-4 items-center">
      <div v-for="resource in resources" :key="resource.id" class="w-10/12">
        <div
          :class="[
            'border border-solid rounded-lg px-4 py-2',
            `resource-card-${resource.id}`,
            {
              '!border-primary-600': activeResource?.id === resource.id,
              'border-gray-300': activeResource?.id !== resource.id
            }
          ]"
          @click="handleSelect(resource)"
        >
          <ResourceContent :resource="resource" :link="false" />
        </div>
      </div>
      <div
        class="bg-white sticky bottom-0 flex items-center w-full justify-center"
      >
        <div
          ref="resizeHandle"
          class="p-4 flex items-center justify-center"
          @mousedown="preventTextSelect"
          @mouseup="enableTextSelect"
          @touchstart="lockScroll"
        >
          <div
            class="w-[40px] h-[5px] rounded-full bg-gray-300 pointer-events-none"
          ></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import interact from "interactjs";
import { nextTick, onMounted, ref, watch } from "vue";

import { useEventCalendar } from "@/SchedulerV2/useEventCalendar";
import { ResourceAPIResponse, TimelineResource } from "@/typings/scheduler";
import ResourceContent from "./ResourceContent.vue";

const { resources, setActiveResource, activeResource } = useEventCalendar();
defineProps<{ resources: ResourceAPIResponse[] }>();
const headerRef = ref<HTMLElement | null>(null);
const resizeHandle = ref<HTMLElement | null>(null);
const minHeight = ref(150);

onMounted(() => {
  if (headerRef.value && resizeHandle.value) {
    interact(headerRef.value).resizable({
      edges: {
        top: false,
        left: false,
        right: false,
        bottom: resizeHandle.value
      },
      listeners: { move: handleResize }
    });
  }
});

const handleResize = (event: Event) => {
  if (!event.target) return;
  Object.assign(event.target.style, {
    height: `${Math.max(minHeight.value, event.rect.height)}px`
  });
  event.target.scrollIntoView({
    behavior: "smooth",
    block: "start",
    inline: "nearest"
  });
};

const lockScroll = (e) => {
  e.preventDefault();
  e.stopPropagation();
  return false;
};

const prevent = (e) => e.preventDefault();

const preventTextSelect = () => {
  window.addEventListener("selectstart", prevent);
};

const enableTextSelect = () => {
  window.removeEventListener("selectstart", prevent);
};

const handleSelect = (resource) => {
  setActiveResource(resource);
  if (headerRef.value) {
    headerRef.value.style.height = `${minHeight.value}px`;
  }
};

watch(
  activeResource,
  async (newVal: TimelineResource, oldVal: TimelineResource) => {
    if (newVal?.id !== oldVal?.id && newVal) {
      await nextTick();
      document.querySelector(`.resource-card-${newVal.id}`)?.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest"
      });
    }
  }
);
</script>

<style lang="scss"></style>
